import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import api from '../../services/api';

const AccionSolicitudPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const accion = searchParams.get('accion');
  
  const [status, setStatus] = useState('loading'); // loading, confirmando, success, error, ya_procesada
  const [resultado, setResultado] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [showMotivoInput, setShowMotivoInput] = useState(false);

  useEffect(() => {
    if (!token || !accion) {
      setStatus('error');
      setResultado({ message: 'Enlace inválido. Faltan parámetros.' });
      return;
    }

    if (accion === 'aprobar') {
      // Para aprobar, ejecutamos directamente
      ejecutarAccion();
    } else if (accion === 'rechazar') {
      // Para rechazar, mostramos confirmación con opción de motivo
      setStatus('confirmando');
    } else {
      setStatus('error');
      setResultado({ message: 'Acción no reconocida.' });
    }
  }, [token, accion]);

  const ejecutarAccion = async (motivo = '') => {
    setStatus('loading');
    try {
      let response;
      if (accion === 'aprobar') {
        response = await api.get(`/solicitudes/suscripcion/accion/aprobar/${token}`);
      } else {
        response = await api.get(`/solicitudes/suscripcion/accion/rechazar/${token}?motivo=${encodeURIComponent(motivo || 'Solicitud rechazada por el administrador')}`);
      }
      
      if (response.data.ya_procesada) {
        setStatus('ya_procesada');
      } else if (response.data.success) {
        setStatus('success');
      } else {
        setStatus('error');
      }
      setResultado(response.data);
    } catch (error) {
      setStatus('error');
      setResultado({ 
        message: error.response?.data?.detail || 'Error al procesar la solicitud'
      });
    }
  };

  const handleConfirmarRechazo = () => {
    ejecutarAccion(motivoRechazo);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Loading */}
        {status === 'loading' && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 text-center">
            <Loader2 className="w-16 h-16 text-teal-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {accion === 'aprobar' ? 'Aprobando solicitud...' : 'Procesando...'}
            </h2>
            <p className="text-slate-400">Por favor espera un momento</p>
          </div>
        )}

        {/* Confirmación para rechazar */}
        {status === 'confirmando' && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                ¿Rechazar esta solicitud?
              </h2>
              <p className="text-slate-400">
                Esta acción enviará un email al solicitante informándole del rechazo.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Motivo del rechazo (opcional)
                </label>
                <textarea
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Ej: Documentación incompleta, datos incorrectos..."
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarRechazo}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Confirmar Rechazo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 text-center">
            <div className={`w-16 h-16 ${accion === 'aprobar' ? 'bg-green-500/20' : 'bg-red-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {accion === 'aprobar' ? (
                <CheckCircle className="w-10 h-10 text-green-500" />
              ) : (
                <XCircle className="w-10 h-10 text-red-500" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {accion === 'aprobar' ? '¡Solicitud Aprobada!' : 'Solicitud Rechazada'}
            </h2>
            
            {resultado && (
              <div className="mt-4 p-4 bg-slate-700/50 rounded-lg text-left">
                <p className="text-slate-300 mb-2">
                  <span className="text-slate-400">Taller:</span> {resultado.razon_social}
                </p>
                {resultado.nivel && (
                  <p className="text-slate-300 mb-2">
                    <span className="text-slate-400">Nivel:</span> {resultado.nivel.toUpperCase()}
                  </p>
                )}
                {resultado.email_usuario && (
                  <p className="text-slate-300">
                    <span className="text-slate-400">Email:</span> {resultado.email_usuario}
                  </p>
                )}
                {resultado.motivo && (
                  <p className="text-slate-300">
                    <span className="text-slate-400">Motivo:</span> {resultado.motivo}
                  </p>
                )}
              </div>
            )}

            <p className="text-slate-400 mt-4 text-sm">
              {accion === 'aprobar' 
                ? 'Se ha enviado un email al usuario con sus credenciales de acceso.'
                : 'Se ha notificado al solicitante sobre esta decisión.'}
            </p>

            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Inicio
            </button>
          </div>
        )}

        {/* Ya procesada */}
        {status === 'ya_procesada' && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Solicitud ya procesada
            </h2>
            <p className="text-slate-400 mb-4">
              {resultado?.message || 'Esta solicitud ya fue procesada anteriormente.'}
            </p>
            {resultado?.estado && (
              <p className="text-slate-300">
                Estado actual: <span className={`font-semibold ${resultado.estado === 'aprobada' ? 'text-green-400' : 'text-red-400'}`}>
                  {resultado.estado.toUpperCase()}
                </span>
              </p>
            )}
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Inicio
            </button>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Error
            </h2>
            <p className="text-slate-400">
              {resultado?.message || 'No se pudo procesar la solicitud. El enlace puede haber expirado o ser inválido.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccionSolicitudPage;
